// [CONV-SCAN-DECLS] Structural decl-walking primitives shared by the regex
// source parsers (C#, Dart, Go, Protobuf, Python, F#).
//
// Every `fromSource` re-implements the same offset-driven walk: run a global
// regex over the source, collect each match with its `m.index` offset, and
// (usually) emit the collected decls in source order. The brace-delimited
// languages additionally track which byte ranges have been consumed by an
// outer block so an inner scan doesn't re-pick nested decls. Only the regexes,
// the per-match projection, and the field/type mapping differ per language —
// the walk itself is identical, so it lives here.
import { extractBalancedBlock } from "./brace-lang.js";
/**
 * Run `re` globally over `source`, projecting each match through `project`
 * (which may return null to skip). Resets `re.lastIndex` first so callers can
 * reuse module-level regexes. Matches are returned in source order.
 */
export const scanAll = (re, source, project) => {
    re.lastIndex = 0;
    const out = [];
    let m;
    while ((m = re.exec(source)) !== null) {
        const projected = project(m);
        if (projected !== null) {
            out.push(projected);
        }
    }
    return out;
};
/** Order collected decls by their source offset (ascending). */
export const orderBySource = (decls) => [...decls].sort((a, b) => a.offset - b.offset);
/** Create a fresh {@link ConsumedTracker}. */
export const makeConsumedTracker = () => {
    const ranges = [];
    return {
        consume: (start, end) => {
            ranges.push([start, end]);
        },
        isInside: (idx) => ranges.some(([start, end]) => idx >= start && idx < end),
    };
};
/**
 * Scan for brace-block heads: for each `re` match ending on `{`, extract the
 * balanced `{ … }` body and yield a {@link BlockMatch}. Matches inside a range
 * already consumed by `opts.skip` are dropped; each yielded match's range is
 * recorded in `opts.mark` so later scans can skip its nested decls.
 */
export const scanBraceBlocks = (source, re, opts = {}) => scanAll(re, source, (m) => toBlockMatch(source, m, opts));
const toBlockMatch = (source, m, opts) => {
    if (opts.skip?.isInside(m.index) === true) {
        return null;
    }
    const full = m[0];
    const openIdx = m.index + full.length - 1;
    const body = extractBalancedBlock(source, openIdx, "{", "}");
    /* v8 ignore next 3 — a `{`-anchored regex guarantees a balanced `}` */
    if (body === null) {
        return null;
    }
    const endOffset = openIdx + body.length + 2;
    opts.mark?.consume(m.index, endOffset);
    return { groups: m.slice(1), body, offset: m.index, endOffset };
};
/**
 * Parse a block/DU body into `{ name, type }` field records. Splits on the
 * given separator, trims each part, drops blanks and `commentPrefix` lines,
 * matches `fieldRe` (name in group 1, type in group 2), and maps the raw type
 * through `mapType`. Parts that don't match `fieldRe` are dropped.
 */
export const parseFields = (body, opts) => body
    .split(opts.separator)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith(opts.commentPrefix))
    .map((l) => opts.fieldRe.exec(l))
    .flatMap((m) => (m === null ? [] : fieldFromMatch(m, opts.mapType)));
const fieldFromMatch = (m, mapType) => {
    const [, name, type] = m;
    return name === undefined || type === undefined ? [] : [{ name, type: mapType(type) }];
};
//# sourceMappingURL=scan-decls.js.map